import {
  Building2,
  BriefcaseMedical,
  ClipboardCheck,
  Database,
  Home,
  RefreshCw,
  ShieldCheck,
  Stethoscope,
  Users,
} from "lucide-react";

import {
  useEffect,
  useState,
} from "react";

import CountUp from "react-countup";

import {
  useNavigate,
} from "react-router-dom";

import useFacilities from "../hooks/useFacilities";

import "../styles/AdminDashboard.css";

function AdminDashboard() {
  const navigate =
    useNavigate();

  const {
    statistics,
    loading,
    loadFacilities,
  } = useFacilities();

  const [
    user,
    setUser,
  ] = useState(null);

  const [
    error,
    setError,
  ] = useState("");

  const [refreshing, setRefreshing] = useState(false);
const [refreshVersion, setRefreshVersion] = useState(0);

  useEffect(() => {
    try {
      const storedUser =
        localStorage.getItem(
          "zethUser"
        );

      if (storedUser) {
        const parsedUser =
          JSON.parse(
            storedUser
          );

        setUser(
          parsedUser
        );

        if (
          parsedUser?.role !==
          "admin"
        ) {
          navigate(
            "/admin/login",
            {
              replace: true,
            }
          );

          return;
        }
      } else {
        navigate(
          "/admin/login",
          {
            replace: true,
          }
        );

        return;
      }

      loadFacilities({
        includeInactive: true,
        limit: 100,
      }).catch(
        (loadError) => {
          setError(
            loadError?.message ||
              "Unable to load dashboard data."
          );
        }
      );
    } catch (storageError) {
      console.error(
        "Admin dashboard initialization error:",
        storageError
      );

      navigate(
        "/admin/login",
        {
          replace: true,
        }
      );
    }
  }, [
    loadFacilities,
    navigate,
  ]);

  const handleRefresh = async () => {
  try {
    setRefreshing(true);
    setError("");

    await loadFacilities({
      includeInactive: true,
      limit: 100,
    });

    setRefreshVersion((current) => current + 1);
  } catch (loadError) {
    setError(
      loadError?.message ||
        "Unable to refresh dashboard data."
    );
  } finally {
    setRefreshing(false);
  }
};
   
  const quickActions = [
    {
      title:
        "Facility Management",

      description:
        "Manage healthcare facilities, approvals and operational status.",

      icon:
        Building2,

      action: () =>
        navigate(
          "/admin/facilities"
        ),
    },

    {
      title:
        "Master Data",

      description:
        "Manage professions and clinical specialties.",

      icon:
        Database,

      action: () =>
        navigate(
          "/admin/master-data"
        ),
    },

    {
      title:
        "Home",

      description:
        "Return to the Zeth Health public home page.",

      icon:
        Home,

      action: () =>
        navigate("/"),
    },
  ];

  return (
    <main className="admin-dashboard-page">
      <header className="admin-dashboard-header">
        <div>
          <p className="admin-dashboard-eyebrow">
            ZETH HEALTH ADMINISTRATION
          </p>

          <h1>
            Admin Dashboard
          </h1>

          <p className="admin-dashboard-subtitle">
            Monitor healthcare workforce operations,
            facility management, compliance and
            platform administration.
          </p>
        </div>

       <div className="admin-dashboard-header-actions">
 <button
  type="button"
  className="admin-dashboard-btn secondary"
  onClick={handleRefresh}
  disabled={refreshing}
>
  <RefreshCw
    size={17}
    className={refreshing ? "is-spinning" : ""}
  />

  {refreshing ? "Refreshing..." : "Refresh"}
</button>
</div>
      </header>

      <section className="admin-dashboard-welcome">
        <div className="admin-dashboard-welcome-icon">
          <ShieldCheck
            size={28}
          />
        </div>

        <div>
          <p>
            Signed in as
          </p>

          <strong>
            {user?.firstName ||
              "Zeth"}{" "}
            {user?.lastName ||
              "Administrator"}
          </strong>

          <span>
            {user?.email ||
              "admin@zethhealth.ca"}
          </span>
        </div>
      </section>

      {error && (
        <div className="admin-dashboard-error">
          {error}
        </div>
      )}

      <section className="admin-dashboard-stat-grid">
        <article className="admin-dashboard-stat-card">
          <div className="admin-dashboard-stat-icon">
            <Building2
              size={24}
            />
          </div>

          <div>
            <strong key={`total-${refreshVersion}`}>
  <CountUp
    start={0}
    end={Number(statistics.total || 0)}
    duration={0.7}
  />
</strong>

            <span>
              Total Facilities
            </span>
          </div>
        </article>

        <article className="admin-dashboard-stat-card">
          <div className="admin-dashboard-stat-icon">
            <ClipboardCheck
              size={24}
            />
          </div>

          <div>
            <strong key={`active-${refreshVersion}`}>
  <CountUp
    start={0}
    end={Number(statistics.active || 0)}
    duration={0.7}
  />
</strong>

            <span>
              Active Facilities
            </span>
          </div>
        </article>

        <article className="admin-dashboard-stat-card">
          <div className="admin-dashboard-stat-icon">
            <ShieldCheck
              size={24}
            />
          </div>

          <div>
            <strong key={`pending-${refreshVersion}`}>
  <CountUp
    start={0}
    end={Number(statistics.pending_approval || 0)}
    duration={0.7}
  />
</strong>

            <span>
              Pending Approvals
            </span>
          </div>
        </article>

        <article className="admin-dashboard-stat-card">
          <div className="admin-dashboard-stat-icon">
            <BriefcaseMedical
              size={24}
            />
          </div>

          <div>
            <strong key={`hospitals-${refreshVersion}`}>
  <CountUp
    start={0}
    end={Number(statistics.hospitals || 0)}
    duration={0.7}
  />
</strong>

            <span>
              Hospitals
            </span>
          </div>
        </article>
      </section>

      <section className="admin-dashboard-content-grid">
        <div className="admin-dashboard-section">
          <div className="admin-dashboard-section-heading">
            <div>
              <p className="admin-dashboard-section-eyebrow">
                OPERATIONS
              </p>

              <h2>
                Quick Actions
              </h2>
            </div>
          </div>

          <div className="admin-dashboard-action-grid">
            {quickActions.map(
              (item) => {
                const Icon =
                  item.icon;

                return (
                  <button
                    type="button"
                    key={
                      item.title
                    }
                    className="admin-dashboard-action-card"
                    onClick={
                      item.action
                    }
                  >
                    <span className="admin-dashboard-action-icon">
                      <Icon
                        size={23}
                      />
                    </span>

                    <span className="admin-dashboard-action-copy">
                      <strong>
                        {
                          item.title
                        }
                      </strong>

                      <small>
                        {
                          item.description
                        }
                      </small>
                    </span>
                  </button>
                );
              }
            )}
          </div>
        </div>

        <div className="admin-dashboard-section">
          <div className="admin-dashboard-section-heading">
            <div>
              <p className="admin-dashboard-section-eyebrow">
                PLATFORM
              </p>

              <h2>
                Administration Modules
              </h2>
            </div>
          </div>

          <div className="admin-dashboard-module-list">
            <button
              type="button"
              onClick={() =>
                navigate(
                  "/admin/facilities"
                )
              }
            >
              <Building2
                size={21}
              />

              <div>
                <strong>
                  Facilities
                </strong>

                <span>
                  Manage facilities,
                  approvals, contracts
                  and contacts.
                </span>
              </div>
            </button>

            <button
              type="button"
              onClick={() =>
                navigate(
                  "/admin/master-data"
                )
              }
            >
              <Stethoscope
                size={21}
              />

              <div>
                <strong>
                  Master Data
                </strong>

                <span>
                  Manage professions
                  and specialties.
                </span>
              </div>
            </button>

            <button
              type="button"
              disabled
            >
              <Users
                size={21}
              />

              <div>
                <strong>
                  Professionals
                </strong>

                <span>
                  Professional management
                  module coming next.
                </span>
              </div>
            </button>

            <button
              type="button"
              disabled
            >
              <BriefcaseMedical
                size={21}
              />

              <div>
                <strong>
                  Assignments
                </strong>

                <span>
                  Assignment administration
                  module coming next.
                </span>
              </div>
            </button>
          </div>
        </div>
      </section>
    </main>
  );
}

export default AdminDashboard;